/**
 * GAM Brand v2 — Skin Tokens
 *
 * Source of truth: docs/BRAND.md (Layer 2 — Tokens)
 *
 * 3 skins (Quiet / Pro / Signature) that bundle CSS variable values.
 * When the active ContentSkin is one of these, applyGamTokens() writes
 * the tokens to <html> as CSS custom properties.
 *
 * Foundation (teal primary, orange signature, Heebo, neutrals) is NOT
 * controlled here — that's the locked brand v2. These tokens only
 * adjust the "feel": density, radius, decoration, blur, spring, shadow.
 */

import type { ContentSkinId } from "./types";
import { applyStoredDecoration } from "@/lib/appearance/options";

export type GamSkinId = "gam-quiet" | "gam-pro" | "gam-signature" | "gam-soft";

/** All ContentSkinIds that are GAM-branded. */
export const GAM_SKIN_IDS: readonly GamSkinId[] = ["gam-quiet", "gam-pro", "gam-signature", "gam-soft"] as const;

export function isGamSkin(id: ContentSkinId): id is GamSkinId {
  return (GAM_SKIN_IDS as readonly string[]).includes(id);
}

/** CSS variables that GAM skins write. Documented in docs/BRAND.md. */
export interface GamSkinTokens {
  // Density
  "--gam-density-pad-card": string;
  "--gam-density-pad-button": string;
  "--gam-density-gap-grid": string;
  // Radius
  "--gam-radius-btn": string;
  "--gam-radius-input": string;
  "--gam-radius-card": string;
  "--gam-radius-hero": string;
  // Decoration (orange line accents) — opacities 0..1
  "--gam-decoration-spine": string;
  "--gam-decoration-slash": string;
  "--gam-decoration-arch": string;
  // Blur (frosted glass on chrome)
  "--gam-blur-chrome": string;
  // Spring (motion personality)
  "--gam-spring-default": string;
  "--gam-spring-bounce": string;
  // Shadow
  "--gam-shadow-card": string;
  "--gam-shadow-card-hover": string;
  // Hero progress shimmer (0 = off, 1 = full)
  "--gam-hero-shimmer": string;
}

export const GAM_SKIN_TOKENS: Record<GamSkinId, GamSkinTokens> = {
  "gam-quiet": {
    "--gam-density-pad-card": "24px",
    "--gam-density-pad-button": "12px 24px",
    "--gam-density-gap-grid": "24px",
    "--gam-radius-btn": "12px",
    "--gam-radius-input": "16px",
    "--gam-radius-card": "20px",
    "--gam-radius-hero": "28px",
    "--gam-decoration-spine": "0.55",
    "--gam-decoration-slash": "0",
    "--gam-decoration-arch": "0",
    "--gam-blur-chrome": "blur(40px) saturate(180%)",
    "--gam-spring-default": "cubic-bezier(0.32, 0.72, 0, 1)",
    "--gam-spring-bounce": "cubic-bezier(0.32, 0.72, 0, 1)",
    "--gam-shadow-card": "0 4px 12px rgba(0,0,0,0.3)",
    "--gam-shadow-card-hover": "0 8px 20px rgba(0,0,0,0.4)",
    "--gam-hero-shimmer": "0",
  },
  "gam-pro": {
    "--gam-density-pad-card": "16px",
    "--gam-density-pad-button": "8px 16px",
    "--gam-density-gap-grid": "12px",
    "--gam-radius-btn": "8px",
    "--gam-radius-input": "10px",
    "--gam-radius-card": "14px",
    "--gam-radius-hero": "18px",
    "--gam-decoration-spine": "0.65",
    "--gam-decoration-slash": "0",
    "--gam-decoration-arch": "0.4",
    "--gam-blur-chrome": "blur(24px) saturate(160%)",
    "--gam-spring-default": "cubic-bezier(0.4, 0, 0.2, 1)",
    "--gam-spring-bounce": "cubic-bezier(0.4, 0, 0.2, 1)",
    "--gam-shadow-card": "0 4px 16px rgba(0,0,0,0.4)",
    "--gam-shadow-card-hover": "0 12px 28px rgba(0,0,0,0.5)",
    "--gam-hero-shimmer": "0.3",
  },
  "gam-signature": {
    "--gam-density-pad-card": "20px",
    "--gam-density-pad-button": "10px 20px",
    "--gam-density-gap-grid": "20px",
    "--gam-radius-btn": "12px",
    "--gam-radius-input": "14px",
    "--gam-radius-card": "20px",
    "--gam-radius-hero": "32px",
    "--gam-decoration-spine": "0.7",
    "--gam-decoration-slash": "0.8",
    "--gam-decoration-arch": "0.6",
    "--gam-blur-chrome": "blur(32px) saturate(180%)",
    "--gam-spring-default": "cubic-bezier(0.32, 0.72, 0, 1)",
    "--gam-spring-bounce": "cubic-bezier(0.34, 1.56, 0.64, 1)",
    "--gam-shadow-card": "0 4px 16px rgba(0,0,0,0.4), 0 0 0 1px rgba(20,184,166,0.05)",
    "--gam-shadow-card-hover": "0 12px 32px rgba(0,0,0,0.5), 0 0 24px rgba(20,184,166,0.15)",
    "--gam-hero-shimmer": "0.7",
  },
  "gam-soft": {
    // Shape borrowed from a shadcnblocks theme export. Palette/typeface rejected —
    // those are Layer 1 and locked. See docs/design-library/sources.md.
    "--gam-density-pad-card": "16px",
    "--gam-density-pad-button": "10px 20px",
    "--gam-density-gap-grid": "16px",
    "--gam-radius-btn": "8px",
    "--gam-radius-input": "8px",
    "--gam-radius-card": "12px",
    "--gam-radius-hero": "16px",
    "--gam-decoration-spine": "0.55",
    "--gam-decoration-slash": "0",
    "--gam-decoration-arch": "0",
    "--gam-blur-chrome": "blur(24px) saturate(160%)",
    "--gam-spring-default": "cubic-bezier(0.32, 0.72, 0, 1)",
    "--gam-spring-bounce": "cubic-bezier(0.32, 0.72, 0, 1)",
    "--gam-shadow-card": "0px 4px 8px -2px rgba(0,0,0,0.35), 0px 1px 2px -3px rgba(0,0,0,0.30)",
    "--gam-shadow-card-hover": "0px 4px 8px -2px rgba(0,0,0,0.35), 0px 8px 10px -3px rgba(0,0,0,0.35)",
    "--gam-hero-shimmer": "0.2",
  },
};

/**
 * Apply GAM token CSS variables to the document root.
 * Call this when the active content skin is a GAM skin.
 * Safe to call repeatedly — only sets properties, doesn't trigger re-render.
 */
export function applyGamTokens(skin: GamSkinId): void {
  if (typeof document === "undefined") return;
  const tokens = GAM_SKIN_TOKENS[skin];
  const style = document.documentElement.style;
  for (const [key, value] of Object.entries(tokens)) {
    style.setProperty(key, value);
  }
  // A decoration picked from appearance_decorations sits on top of the skin.
  // Every skin write goes through here, so the choice survives skin changes.
  applyStoredDecoration();
}

/**
 * Remove GAM token CSS variables from the document root.
 * Call this when switching away from a GAM skin so non-GAM skins don't
 * inherit stale token values.
 */
export function clearGamTokens(): void {
  if (typeof document === "undefined") return;
  const style = document.documentElement.style;
  const sample = GAM_SKIN_TOKENS["gam-quiet"];
  for (const key of Object.keys(sample)) {
    style.removeProperty(key);
  }
}
